import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table, ProgressBar } from 'react-bootstrap';
import { FaCheckCircle, FaIdCard, FaFileUpload, FaHistory, FaShieldAlt, FaEye, FaUniversity, FaCamera, FaUserCheck, FaFingerprint, FaGlobe, FaPhone, FaEnvelope } from 'react-icons/fa';

const VerificationsModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('document_verification');
  const [verificationStep, setVerificationStep] = useState(1); // 1=Select Service, 2=Upload Documents, 3=Processing, 4=Results
  const [selectedService, setSelectedService] = useState('');
  const [personalDetails, setPersonalDetails] = useState({
    name: '',
    mobile: '',
    email: '',
    address: '',
    pincode: ''
  });
  const [documents, setDocuments] = useState({
    primaryDoc: null,
    secondaryDoc: null,
    photograph: null,
    signature: null
  });
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [verificationHistory, setVerificationHistory] = useState([]);
  const [verificationResults, setVerificationResults] = useState(null);

  // Verification Services
  const verificationServices = [
    {
      id: 'pan_verification',
      name: 'PAN Card Verification',
      description: 'Verify PAN card details with Income Tax Department',
      icon: <FaIdCard className="text-blue-600" />,
      charges: 5,
      timeEstimate: '2-3 minutes',
      requiredDocs: ['PAN Card'],
      category: 'document'
    },
    {
      id: 'aadhar_verification',
      name: 'Aadhaar Verification',
      description: 'Verify Aadhaar details with UIDAI',
      icon: <FaFingerprint className="text-green-600" />,
      charges: 3,
      timeEstimate: '1-2 minutes',
      requiredDocs: ['Aadhaar Card'],
      category: 'document'
    },
    {
      id: 'bank_account_verification',
      name: 'Bank Account Verification',
      description: 'Verify bank account details and status',
      icon: <FaUniversity className="text-purple-600" />,
      charges: 8,
      timeEstimate: '3-5 minutes',
      requiredDocs: ['Bank Statement', 'Cancelled Cheque'],
      category: 'financial'
    },
    {
      id: 'gst_verification',
      name: 'GST Number Verification',
      description: 'Verify GST registration details',
      icon: <FaShieldAlt className="text-orange-600" />,
      charges: 10,
      timeEstimate: '2-4 minutes',
      requiredDocs: ['GST Certificate'],
      category: 'business'
    },
    {
      id: 'driving_license_verification',
      name: 'Driving License Verification',
      description: 'Verify driving license with RTO database',
      icon: <FaIdCard className="text-red-600" />,
      charges: 7,
      timeEstimate: '3-5 minutes',
      requiredDocs: ['Driving License'],
      category: 'document'
    },
    {
      id: 'voter_id_verification',
      name: 'Voter ID Verification',
      description: 'Verify voter ID with Election Commission',
      icon: <FaUserCheck className="text-indigo-600" />,
      charges: 5,
      timeEstimate: '2-3 minutes',
      requiredDocs: ['Voter ID Card'],
      category: 'document'
    },
    {
      id: 'passport_verification',
      name: 'Passport Verification',
      description: 'Verify passport details with MEA database',
      icon: <FaGlobe className="text-teal-600" />,
      charges: 15,
      timeEstimate: '5-10 minutes',
      requiredDocs: ['Passport'],
      category: 'document'
    },
    {
      id: 'mobile_verification',
      name: 'Mobile Number Verification',
      description: 'Verify mobile number ownership via OTP',
      icon: <FaPhone className="text-pink-600" />,
      charges: 2,
      timeEstimate: '1 minute',
      requiredDocs: ['Mobile Number'],
      category: 'contact'
    },
    {
      id: 'email_verification',
      name: 'Email Verification',
      description: 'Verify email address ownership',
      icon: <FaEnvelope className="text-cyan-600" />,
      charges: 2,
      timeEstimate: '1-2 minutes',
      requiredDocs: ['Email Address'],
      category: 'contact'
    },
    {
      id: 'address_verification',
      name: 'Address Verification',
      description: 'Verify residential/business address',
      icon: <FaEye className="text-yellow-600" />,
      charges: 20,
      timeEstimate: '1-2 days',
      requiredDocs: ['Address Proof', 'Photograph'],
      category: 'physical'
    }
  ];

  const verificationCategories = [
    { key: 'document_verification', label: 'Document Verification', icon: <FaIdCard className="text-primary" /> },
    { key: 'financial_verification', label: 'Financial Verification', icon: <FaUniversity className="text-success" /> },
    { key: 'identity_verification', label: 'Identity Verification', icon: <FaUserCheck className="text-warning" /> },
    { key: 'address_verification', label: 'Address Verification', icon: <FaEye className="text-info" /> }
  ];

  const mockVerificationHistory = [
    {
      id: 'VER001',
      service: 'PAN Card Verification',
      reference: 'ABCDE1234F',
      status: 'Verified',
      charges: 5,
      date: '2025-01-15',
      time: '14:30:25'
    },
    {
      id: 'VER002',
      service: 'Aadhaar Verification',
      reference: '****8765',
      status: 'Verified',
      charges: 3,
      date: '2025-01-15',
      time: '10:15:42'
    },
    {
      id: 'VER003',
      service: 'Bank Account Verification',
      reference: '****5678',
      status: 'Failed',
      charges: 8,
      date: '2025-01-14',
      time: '16:45:12'
    }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVerificationStep(1);
    setSelectedService('');
    setMessage({ type: '', text: '' });
    
    if (tab === 'history') {
      setVerificationHistory(mockVerificationHistory);
    }
  };

  const validatePersonalDetails = () => {
    const { name, mobile, email } = personalDetails;
    
    if (!name.trim()) return 'Please enter your name';
    if (!/^[6-9]\d{9}$/.test(mobile)) return 'Please enter a valid mobile number';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email';
    
    return null;
  };

  const validateDocuments = () => {
    const service = verificationServices.find(s => s.id === selectedService);
    if (!service) return 'Please select a verification service';
    
    if (!documents.primaryDoc) return `Please upload ${service.requiredDocs[0]}`;
    
    // Check if secondary document is required
    if (service.requiredDocs.length > 1 && !documents.secondaryDoc) {
      return `Please upload ${service.requiredDocs[1]}`;
    }
    
    return null;
  };

  const nextStep = () => {
    if (verificationStep === 1) {
      if (!selectedService) {
        setMessage({ type: 'error', text: 'Please select a verification service' });
        return;
      }
      const validation = validatePersonalDetails();
      if (validation) {
        setMessage({ type: 'error', text: validation });
        return;
      }
    } else if (verificationStep === 2) {
      const validation = validateDocuments();
      if (validation) {
        setMessage({ type: 'error', text: validation });
        return;
      }
    }
    
    setVerificationStep(verificationStep + 1);
    setMessage({ type: '', text: '' });
  };

  const prevStep = () => {
    setVerificationStep(verificationStep - 1);
    setMessage({ type: '', text: '' });
  };

  const startVerification = async () => {
    setLoading(true);
    setVerificationProgress(0);
    setMessage({ type: 'info', text: 'Starting verification process...' });

    try {
      const service = verificationServices.find(s => s.id === selectedService);
      
      // Simulate verification process with progress updates
      const steps = [
        'Uploading documents...',
        'Validating document format...',
        'Connecting to verification database...',
        'Processing verification request...',
        'Validating information...',
        'Generating verification report...'
      ];

      for (let i = 0; i < steps.length; i++) {
        setMessage({ type: 'info', text: steps[i] });
        setVerificationProgress(((i + 1) / steps.length) * 100);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Generate verification results
      const isSuccessful = Math.random() > 0.2; // 80% success rate
      const results = {
        service: service.name,
        reference: generateReference(selectedService),
        status: isSuccessful ? 'Verified' : 'Failed',
        details: generateVerificationDetails(selectedService, isSuccessful),
        confidence: isSuccessful ? Math.floor(Math.random() * 20 + 80) : Math.floor(Math.random() * 50 + 20),
        timestamp: new Date().toLocaleString(),
        charges: service.charges
      };

      setVerificationResults(results);
      setVerificationStep(4);
      setMessage({ 
        type: isSuccessful ? 'success' : 'warning', 
        text: `Verification ${isSuccessful ? 'completed successfully' : 'completed with issues'}` 
      });

      if (onSuccess && isSuccessful) {
        onSuccess(results);
      }

    } catch (error) {
      setMessage({ type: 'error', text: 'Verification failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const generateReference = (serviceId) => {
    const prefixes = {
      pan_verification: 'PAN',
      aadhar_verification: 'AAD',
      bank_account_verification: 'BNK',
      gst_verification: 'GST',
      driving_license_verification: 'DL',
      voter_id_verification: 'VID',
      passport_verification: 'PP',
      mobile_verification: 'MOB',
      email_verification: 'EML',
      address_verification: 'ADR'
    };
    return (prefixes[serviceId] || 'VER') + Date.now().toString().slice(-6);
  };

  const generateVerificationDetails = (serviceId, isSuccessful) => {
    if (!isSuccessful) {
      return ['Document quality issues detected', 'Information mismatch found', 'Database connection timeout'];
    }

    const details = {
      pan_verification: ['PAN number is valid and active', 'Name matches with IT records', 'No blacklist entries found'],
      aadhar_verification: ['Aadhaar number is valid', 'Biometric data verified', 'Address confirmed'],
      bank_account_verification: ['Account is active and operational', 'Account holder name verified', 'No fraud alerts'],
      gst_verification: ['GST number is valid and active', 'Business registration confirmed', 'Return filing status: Regular'],
      driving_license_verification: ['License is valid and active', 'No traffic violations', 'Renewal date confirmed'],
      voter_id_verification: ['Voter ID is valid', 'Electoral roll entry confirmed', 'Address verified'],
      passport_verification: ['Passport is valid and active', 'No travel restrictions', 'Biometric data verified'],
      mobile_verification: ['Mobile number is active', 'OTP verification successful', 'Carrier information confirmed'],
      email_verification: ['Email address is valid and active', 'Domain verification successful', 'No spam reports'],
      address_verification: ['Address exists and is valid', 'Postal code confirmed', 'Area classification verified']
    };

    return details[serviceId] || ['Verification completed successfully', 'All checks passed', 'Information validated'];
  };

  const handleFileUpload = (documentType, file) => {
    if (file && file.size > 10 * 1024 * 1024) { // 10MB limit
      setMessage({ type: 'error', text: 'File size should not exceed 10MB' });
      return;
    }
    
    setDocuments({ ...documents, [documentType]: file });
    setMessage({ type: 'success', text: `Document uploaded successfully` });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Verified': 'success',
      'Failed': 'danger',
      'Pending': 'warning',
      'Processing': 'info'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getStepProgress = () => {
    return ((verificationStep - 1) / 3) * 100;
  };

  const handleReset = () => {
    setVerificationStep(1);
    setSelectedService('');
    setPersonalDetails({ name: '', mobile: '', email: '', address: '', pincode: '' });
    setDocuments({ primaryDoc: null, secondaryDoc: null, photograph: null, signature: null });
    setVerificationResults(null);
    setVerificationProgress(0);
    setMessage({ type: '', text: '' });
  };

  const getServicesByCategory = (category) => {
    const categoryMapping = {
      document_verification: ['document'],
      financial_verification: ['financial', 'business'],
      identity_verification: ['document', 'contact'],
      address_verification: ['physical']
    };
    
    return verificationServices.filter(service => 
      categoryMapping[category]?.includes(service.category)
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton className="bg-success text-white">
        <Modal.Title>
          <FaCheckCircle className="me-2" />
          Verification Services
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Verification Category Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          {verificationCategories.map((category) => (
            <Tab 
              key={category.key}
              eventKey={category.key} 
              title={
                <span>
                  {category.icon}
                  <span className="ms-2">{category.label}</span>
                </span>
              }
            >
              <div className="p-2">
                <h6 className="text-success mb-3">{category.label}</h6>
              </div>
            </Tab>
          ))}
          <Tab eventKey="history" title={
            <span>
              <FaHistory className="me-2" />
              Verification History
            </span>
          }>
            <div className="p-2">
              <h6 className="text-success mb-3">Verification History</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Verification History */}
        {activeTab === 'history' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Recent Verifications</h6>
              <Badge bg="success">Total: ₹16</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Verification ID</th>
                  <th>Service</th>
                  <th>Reference</th>
                  <th>Status</th>
                  <th>Charges</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {verificationHistory.map((verification) => (
                  <tr key={verification.id}>
                    <td>{verification.id}</td>
                    <td>{verification.service}</td>
                    <td>{verification.reference}</td>
                    <td>{getStatusBadge(verification.status)}</td>
                    <td>₹{verification.charges}</td>
                    <td>
                      <small>{verification.date}<br/>{verification.time}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Verification Process */}
        {activeTab !== 'history' && (
          <div>
            {/* Progress Bar */}
            {verificationStep <= 3 && (
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span>Verification Progress</span>
                  <span>{verificationStep}/3</span>
                </div>
                <ProgressBar now={getStepProgress()} variant="success" />
                <div className="d-flex justify-content-between mt-1">
                  <small className={verificationStep >= 1 ? 'text-success' : 'text-muted'}>Select Service</small>
                  <small className={verificationStep >= 2 ? 'text-success' : 'text-muted'}>Upload Documents</small>
                  <small className={verificationStep >= 3 ? 'text-success' : 'text-muted'}>Verification</small>
                </div>
              </div>
            )}

            {/* Step 1: Service Selection */}
            {verificationStep === 1 && (
              <Row>
                <Col md={8}>
                  <div className="mb-4">
                    <h6 className="mb-3">Select Verification Service</h6>
                    <Row>
                      {getServicesByCategory(activeTab).map((service) => (
                        <Col md={6} key={service.id} className="mb-3">
                          <Card 
                            className={`h-100 cursor-pointer ${selectedService === service.id ? 'border-success' : ''}`}
                            onClick={() => setSelectedService(service.id)}
                            style={{ cursor: 'pointer' }}
                          >
                            <Card.Body className="p-3">
                              <div className="d-flex align-items-start">
                                <div className="me-3">
                                  {service.icon}
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{service.name}</h6>
                                  <p className="small text-muted mb-2">{service.description}</p>
                                  <div className="d-flex justify-content-between align-items-center">
                                    <Badge bg="info">₹{service.charges}</Badge>
                                    <small className="text-muted">{service.timeEstimate}</small>
                                  </div>
                                  <div className="mt-2">
                                    <small><strong>Required:</strong> {service.requiredDocs.join(', ')}</small>
                                  </div>
                                </div>
                                {selectedService === service.id && (
                                  <FaCheckCircle className="text-success" />
                                )}
                              </div>
                            </Card.Body>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                </Col>

                <Col md={4}>
                  <Card>
                    <Card.Header>
                      <FaUserCheck className="me-2" />
                      Personal Details
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Full Name *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Enter full name"
                          value={personalDetails.name}
                          onChange={(e) => setPersonalDetails({...personalDetails, name: e.target.value})}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Mobile Number *</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="10-digit mobile number"
                          value={personalDetails.mobile}
                          onChange={(e) => setPersonalDetails({
                            ...personalDetails, 
                            mobile: e.target.value.replace(/\D/g, '').slice(0, 10)
                          })}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          placeholder="Enter email address"
                          value={personalDetails.email}
                          onChange={(e) => setPersonalDetails({...personalDetails, email: e.target.value})}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Enter address"
                          value={personalDetails.address}
                          onChange={(e) => setPersonalDetails({...personalDetails, address: e.target.value})}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>Pincode</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="6-digit pincode"
                          value={personalDetails.pincode}
                          onChange={(e) => setPersonalDetails({
                            ...personalDetails, 
                            pincode: e.target.value.replace(/\D/g, '').slice(0, 6)
                          })}
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

            {/* Step 2: Document Upload */}
            {verificationStep === 2 && (
              <Card>
                <Card.Header>
                  <FaFileUpload className="me-2" />
                  Document Upload
                </Card.Header>
                <Card.Body>
                  {(() => {
                    const service = verificationServices.find(s => s.id === selectedService);
                    return (
                      <div>
                        <Alert variant="info" className="mb-4">
                          <FaShieldAlt className="me-2" />
                          <strong>Selected Service:</strong> {service?.name} (₹{service?.charges})<br/>
                          <strong>Required Documents:</strong> {service?.requiredDocs.join(', ')}
                        </Alert>

                        <Row>
                          <Col md={6}>
                            <div className="mb-4">
                              <Form.Label>{service?.requiredDocs[0]} *</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => handleFileUpload('primaryDoc', e.target.files[0])}
                              />
                              {documents.primaryDoc && (
                                <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                              )}
                            </div>

                            {service?.requiredDocs.length > 1 && (
                              <div className="mb-4">
                                <Form.Label>{service.requiredDocs[1]} *</Form.Label>
                                <Form.Control
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => handleFileUpload('secondaryDoc', e.target.files[0])}
                                />
                                {documents.secondaryDoc && (
                                  <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                                )}
                              </div>
                            )}
                          </Col>
                          <Col md={6}>
                            <div className="mb-4">
                              <Form.Label>Photograph (Optional)</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload('photograph', e.target.files[0])}
                              />
                              {documents.photograph && (
                                <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                              )}
                            </div>

                            <div className="mb-4">
                              <Form.Label>Signature (Optional)</Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleFileUpload('signature', e.target.files[0])}
                              />
                              {documents.signature && (
                                <Badge bg="success" className="mt-1">✓ Uploaded</Badge>
                              )}
                            </div>
                          </Col>
                        </Row>

                        <Alert variant="warning">
                          <FaCamera className="me-2" />
                          <strong>Document Guidelines:</strong>
                          <ul className="mb-0 mt-2">
                            <li>Upload clear, readable images or PDFs</li>
                            <li>Ensure all corners and text are visible</li>
                            <li>Maximum file size: 10MB per document</li>
                            <li>Supported formats: JPG, PNG, PDF</li>
                          </ul>
                        </Alert>
                      </div>
                    );
                  })()}
                </Card.Body>
              </Card>
            )}

            {/* Step 3: Verification Processing */}
            {verificationStep === 3 && (
              <Card>
                <Card.Header className="bg-info text-white">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Verification in Progress
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-4">
                    <FaShieldAlt className="text-info mb-3" size={60} />
                    <h5>Processing Your Verification</h5>
                    <p className="text-muted">Please wait while we verify your documents...</p>
                  </div>
                  
                  <ProgressBar 
                    now={verificationProgress} 
                    variant="info" 
                    className="mb-3"
                    style={{ height: '10px' }}
                  />
                  
                  <div className="mb-4">
                    <small className="text-muted">{verificationProgress.toFixed(0)}% Complete</small>
                  </div>

                  {!loading && (
                    <Button 
                      variant="info" 
                      onClick={startVerification}
                      className="me-2"
                    >
                      <FaCheckCircle className="me-2" />
                      Start Verification
                    </Button>
                  )}
                </Card.Body>
              </Card>
            )}

            {/* Step 4: Verification Results */}
            {verificationStep === 4 && verificationResults && (
              <Card>
                <Card.Header className={`text-white ${verificationResults.status === 'Verified' ? 'bg-success' : 'bg-warning'}`}>
                  <FaCheckCircle className="me-2" />
                  Verification Results
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <div className="mb-2"><strong>Service:</strong> {verificationResults.service}</div>
                      <div className="mb-2"><strong>Reference ID:</strong> {verificationResults.reference}</div>
                      <div className="mb-2"><strong>Status:</strong> {getStatusBadge(verificationResults.status)}</div>
                      <div className="mb-2"><strong>Confidence Score:</strong> {verificationResults.confidence}%</div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2"><strong>Verification Time:</strong> {verificationResults.timestamp}</div>
                      <div className="mb-2"><strong>Charges:</strong> ₹{verificationResults.charges}</div>
                      <div className="mb-2"><strong>Report:</strong></div>
                      <ul className="mb-0">
                        {verificationResults.details.map((detail, index) => (
                          <li key={index} className="small text-success">✓ {detail}</li>
                        ))}
                      </ul>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={
            message.type === 'error' ? 'danger' : 
            message.type === 'info' ? 'info' : 
            message.type === 'warning' ? 'warning' : 'success'
          } className="mt-3">
            {message.text}
          </Alert>
        )}

        {/* Security Notice */}
        <Alert variant="success" className="mb-0">
          <FaShieldAlt className="me-2" />
          <strong>Secure Verification:</strong> All document processing is encrypted and complies with data protection regulations. Documents are not stored after verification.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {verificationStep > 1 && verificationStep < 4 && (
              <Button variant="outline-secondary" onClick={prevStep}>
                Previous
              </Button>
            )}
            {verificationStep === 1 && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide} className="me-2">
              Close
            </Button>
            {verificationStep < 3 && activeTab !== 'history' && (
              <Button variant="success" onClick={nextStep}>
                Next
              </Button>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default VerificationsModal;
